import { useState, useEffect, useCallback } from "react";
import { Paper } from "@mui/material";
import { Spinner, Input } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  getInvoiceAuditors,
  createInvoiceAuditor,
  updateInvoiceAuditor,
  deleteInvoiceAuditor,
} from "./Api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FInput = ({ value, onChange, placeholder, type = "text" }) => (
  <Input
    type={type}
    bsSize="sm"
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={{ borderColor: "#E2E8F0", borderRadius: 7, fontSize: "0.82rem" }}
  />
);

const InlineAuditorRow = ({ data, isNew, onSave, onCancel }) => {
  const [row, setRow] = useState({ isActive: true, phone: "", ...data });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setRow((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!row.name?.trim()) { toast.error("Name is required"); return; }
    if (!row.email?.trim() || !EMAIL_RE.test(row.email.trim())) { toast.error("A valid email is required"); return; }
    setSaving(true);
    try { await onSave(row); } finally { setSaving(false); }
  };

  return (
    <tr style={{ background: isNew ? "#ECFDF5" : "#EEF2FF" }}>
      <td style={{ width: 40, textAlign: "center", padding: "8px 10px" }}>
        <FeatherIcon icon={isNew ? "plus" : "edit-2"} size={12} color={isNew ? "#059669" : "#4F46E5"} />
      </td>
      <td style={{ minWidth: 180, padding: "8px 10px" }}>
        <FInput value={row.name} onChange={set("name")} placeholder="Auditor / accounting name…" />
      </td>
      <td style={{ minWidth: 220, padding: "8px 10px" }}>
        <FInput value={row.email} onChange={set("email")} placeholder="accounting@email.com" type="email" />
      </td>
      <td style={{ minWidth: 140, padding: "8px 10px" }}>
        <FInput value={row.phone} onChange={set("phone")} placeholder="Phone (optional)" />
      </td>
      <td style={{ width: 100, textAlign: "center", padding: "8px 10px" }}>
        {isNew ? (
          <span style={{ fontSize: "0.75rem", color: "#94A3B8" }}>Active</span>
        ) : (
          <select
            value={row.isActive ? "true" : "false"}
            onChange={(e) => set("isActive")(e.target.value === "true")}
            style={{
              border: "1px solid #E2E8F0", borderRadius: 7, fontSize: "0.78rem",
              padding: "4px 8px", cursor: "pointer", outline: "none",
              background: row.isActive ? "#ECFDF5" : "#FEF2F2",
              color: row.isActive ? "#059669" : "#DC2626",
              fontWeight: 700, width: "100%",
            }}
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        )}
      </td>
      <td style={{ width: 130, textAlign: "center", padding: "8px 10px" }}>
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          <button type="button" className="iv-btn iv-btn--ghost" onClick={onCancel} disabled={saving}>
            <FeatherIcon icon="x" size={12} /> Cancel
          </button>
          <button type="button" className="iv-btn iv-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={12} />}
            {isNew ? "Add" : "Save"}
          </button>
        </div>
      </td>
    </tr>
  );
};

const InvoiceAuditorsTable = ({ userRole, onRefresh }) => {
  const [auditors, setAuditors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewRow, setShowNewRow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const isCompany = (userRole || "").toLowerCase() === "company";

  const fetchAuditors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvoiceAuditors(showInactive ? {} : { activeOnly: "true" });
      setAuditors(res?.data || []);
    } catch { /* handled */ } finally { setLoading(false); }
  }, [showInactive]);

  useEffect(() => { fetchAuditors(); }, [fetchAuditors]);

  const handleCreate = async (row) => {
    await createInvoiceAuditor({ name: row.name.trim(), email: row.email.trim(), phone: row.phone?.trim() || "" });
    setShowNewRow(false);
    fetchAuditors();
    onRefresh?.();
  };

  const handleUpdate = async (id, row) => {
    await updateInvoiceAuditor(id, { name: row.name.trim(), email: row.email.trim(), phone: row.phone?.trim() || "", isActive: row.isActive });
    setEditingId(null);
    fetchAuditors();
    onRefresh?.();
  };

  const handleDelete = async (a) => {
    const res = await Swal.fire({
      title: "Remove Auditor?",
      html: `<p>Stop sending invoice notifications to <strong>${a.email}</strong>?</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      width: 420,
    });
    if (!res.isConfirmed) return;
    try { await deleteInvoiceAuditor(a._id); fetchAuditors(); onRefresh?.(); } catch { /* handled */ }
  };

  const colSpan = isCompany ? 6 : 5;

  return (
    <Paper elevation={0} className="iv-wrap iv-paper">
      <div className="iv-header">
        <div className="iv-header__left">
          <div className="iv-header__icon-wrap">
            <FeatherIcon icon="shield" size={18} color="#A5B4FC" />
          </div>
          <div className="iv-header__meta">
            <div className="iv-header__label">Finance</div>
            <div className="iv-header__title">Accounting / Auditors</div>
          </div>
        </div>
        <div className="iv-header__actions" style={{ gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: "#475569", fontWeight: 600, cursor: "pointer" }}>
            <Input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} style={{ marginTop: 0 }} />
            Show inactive
          </label>
          {isCompany && (
            <button type="button" className="iv-btn iv-btn--primary" onClick={() => setShowNewRow(true)} disabled={showNewRow}>
              <FeatherIcon icon="plus" size={14} /> New Auditor
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: "10px 16px", background: "#EFF6FF", color: "#1E40AF", fontSize: "0.8rem", borderBottom: "1px solid #BFDBFE" }}>
        These recipients are emailed automatically when invoices are logged and for unpaid-invoice payment reminders.
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="iv-tbl" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: 40 }} />
              <th style={{ minWidth: 180, textAlign: "left", paddingLeft: 10 }}>Name</th>
              <th style={{ minWidth: 220, textAlign: "left", paddingLeft: 10 }}>Email</th>
              <th style={{ minWidth: 140, textAlign: "left", paddingLeft: 10 }}>Phone</th>
              <th style={{ width: 100 }}>Status</th>
              {isCompany && <th style={{ width: 130 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {showNewRow && (
              <InlineAuditorRow isNew data={{ name: "", email: "", phone: "" }} onSave={handleCreate} onCancel={() => setShowNewRow(false)} />
            )}

            {loading && (
              <tr><td colSpan={colSpan} style={{ textAlign: "center", padding: "40px 0" }}><Spinner style={{ color: "#4F46E5" }} /></td></tr>
            )}

            {!loading && auditors.length === 0 && !showNewRow && (
              <tr>
                <td colSpan={colSpan} className="iv-empty-cell">
                  <div className="iv-empty" style={{ minHeight: "20vh" }}>
                    <div className="iv-empty__icon-wrap"><FeatherIcon icon="shield" size={22} color="#4F46E5" /></div>
                    <div className="iv-empty__title">No Auditors</div>
                    <div className="iv-empty__sub">{isCompany ? "Add accounting contacts to receive invoice notifications." : "Ask a company admin to add auditors."}</div>
                  </div>
                </td>
              </tr>
            )}

            {!loading && auditors.map((a) => {
              if (editingId === a._id) {
                return <InlineAuditorRow key={a._id} data={a} onSave={(row) => handleUpdate(a._id, row)} onCancel={() => setEditingId(null)} />;
              }
              return (
                <tr key={a._id} className="iv-row">
                  <td style={{ textAlign: "center", width: 40 }}><FeatherIcon icon="user-check" size={13} color="#94A3B8" /></td>
                  <td style={{ textAlign: "left", paddingLeft: 10, fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>{a.name}</td>
                  <td style={{ textAlign: "left", paddingLeft: 10, color: "#475569", fontSize: "0.8rem" }}>{a.email}</td>
                  <td style={{ textAlign: "left", paddingLeft: 10, color: "#475569", fontSize: "0.8rem" }}>{a.phone || <span style={{ color: "#CBD5E1" }}>—</span>}</td>
                  <td style={{ textAlign: "center" }}>
                    <span style={{
                      background: a.isActive ? "#ECFDF5" : "#FEF2F2",
                      color: a.isActive ? "#059669" : "#DC2626",
                      border: a.isActive ? "1px solid #A7F3D0" : "1px solid #FECDD3",
                      borderRadius: 20, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700,
                    }}>
                      {a.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {isCompany && (
                    <td style={{ textAlign: "center" }}>
                      <div className="iv-action-group">
                        <button type="button" className="iv-ebtn" title="Edit" onClick={() => setEditingId(a._id)}>
                          <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                        </button>
                        {a.isActive && (
                          <button type="button" className="iv-dbtn" title="Remove" onClick={() => handleDelete(a)}>
                            <FeatherIcon icon="x-circle" size={13} color="#DC2626" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Paper>
  );
};

export { InvoiceAuditorsTable };
